/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import Layout from "../common/components/Layout";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchClients,
  createClient,
  updateClient,
  toggleClientStatus,
  clearClientError,
} from "../store/slices/clientSlice";

const Client = () => {
  const dispatch = useDispatch();
  const { list: clients = [], status } = useSelector(
    (state) => state.client || {}
  );

  /* ===================== LOCAL STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingClient, setPendingClient] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    id: "",
    clientCode: "",
    clientName: "", 
    details: "",
    contactPerson: "",
    mobile: "",
    email: "",
    address: "",
    website: "",
    isActive: true,
  });

  /* ===================== FILTER & PAGINATION STATE ===================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "clientName",
    direction: "asc",
  });

  useEffect(() => {
    dispatch(fetchClients());
  }, [dispatch]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-2 opacity-25" style={{ fontSize: '0.6rem' }}></i>;
    return sortConfig.direction === "asc" 
      ? <i className="bi bi-sort-down ms-2 text-primary" style={{ fontSize: '0.7rem' }}></i> 
      : <i className="bi bi-sort-up ms-2 text-primary" style={{ fontSize: '0.7rem' }}></i>;
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const openAddModal = () => {
    dispatch(clearClientError());
    setIsEditMode(false);
    setFormData({
      id: "", clientCode: "", clientName: "", details: "",
      contactPerson: "", mobile: "", email: "", address: "",
      website: "", isActive: true,
    });
    setShowModal(true);
  };

  const openEditModal = (client) => {
    dispatch(clearClientError());
    setIsEditMode(true);
    setFormData({
      id: client.id,
      clientCode: client.clientCode || "",
      clientName: client.name || client.clientName || "", 
      details: client.details || "",
      contactPerson: client.contactPerson || "",
      mobile: client.mobile || "",
      email: client.email || "",
      address: client.address || "",
      website: client.website || "",
      isActive: (client.status === "Active" || client.isActive === true),
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Explicit Payload Mapping to ensure 'clientName' is passed to the slice
    const payload = {
      clientName: formData.clientName,
      clientCode: formData.clientCode,
      details: formData.details,
      address: formData.address,
      mobile: formData.mobile,
      email: formData.email,
      contactPerson: formData.contactPerson,
      website: formData.website,
      isActive: formData.isActive
    };

    try {
      if (isEditMode) {
        await dispatch(updateClient({ id: formData.id, data: payload })).unwrap();
        toast.success(`${formData.clientName} updated!`);
      } else {
        await dispatch(createClient(payload)).unwrap();
        toast.success(`${formData.clientName} created!`);
      }
      setShowModal(false);
      dispatch(fetchClients());
    } catch (err) {
      toast.error(err || "Failed to save client");
    }
  };

  const handleStatusToggle = (client) => {
    const currentStatus = client.status || (client.isActive ? "Active" : "Inactive");
    if (currentStatus === "Active") {
      setPendingClient(client);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(client);
    }
  };

  const executeStatusUpdate = async (client) => {
    try {
      const currentStatus = client.status || (client.isActive ? "Active" : "Inactive");
      await dispatch(toggleClientStatus({ id: client.id, currentStatus })).unwrap();
      toast.success(`${client.name || client.clientName} status updated!`);
      setShowConfirm(false);
      dispatch(fetchClients());
    } catch (err) {
      toast.error("Update failed");
    }
  };

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const processedData = useMemo(() => {
    let items = Array.isArray(clients) ? [...clients] : [];
    items = items.filter((item) => {
      const name = item.name || item.clientName || "";
      const matchesSearch =
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.clientCode || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.contactPerson || "").toLowerCase().includes(searchTerm.toLowerCase());
      const itemStatus = item.status || (item.isActive ? "Active" : "Inactive");
      const matchesStatus = statusFilter === "All" || itemStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });

    items.sort((a, b) => {
      const valA = String(a[sortConfig.key] || a.name || "").toLowerCase();
      const valB = String(b[sortConfig.key] || b.name || "").toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return items;
  }, [clients, searchTerm, statusFilter, sortConfig]);

  const totalItems = processedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentData = processedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getPaginationGroup = () => {
    const totalDisplayed = 5; 
    let start = Math.max(currentPage - Math.floor(totalDisplayed / 2), 1);
    let end = Math.min(start + totalDisplayed - 1, totalPages);
    if (end === totalPages) start = Math.max(end - totalDisplayed + 1, 1);
    const pages = [];
    for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
    return pages;
  };

  const inputStyle = { border: "1px solid rgba(0,0,0,0.1)", borderRadius: "8px", padding: "10px 14px", fontSize: "0.8rem", backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(5px)", outline: "none" };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="container-fluid py-4 flex-grow-1 px-lg-5 w-100">
        <style>{`
          .custom-table-header th { background-color: #1e293b !important; color: #ffffff !important; cursor: pointer; font-size: 0.7rem !important; font-weight: 700 !important; letter-spacing: 0.05em !important; text-transform: uppercase !important; }
          .page-link-custom { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px !important; margin: 0 3px; font-size: 0.75rem; font-weight: 600; color: #64748b; cursor: pointer; background: transparent; transition: all 0.2s; }
          .page-link-custom.active { background: #0f172a !important; color: #fff !important; }
          .page-link-custom.disabled { opacity: 0.3; cursor: not-allowed; }
          .pagination-container { background: rgba(255, 255, 255, 0.4); border-top: 1px solid rgba(0,0,0,0.05); padding: 1rem 1.5rem; }
        `}</style>

        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0" style={{ letterSpacing: "-1.5px" }}>Payroll Group</h1>
            <p className="text-muted small mb-0">Manage customer accounts and workforce distribution</p>
          </div>
          <button className="btn btn-primary px-4 border-0" style={{ borderRadius: "8px", backgroundColor: "#0f172a", fontWeight: "600", fontSize: '0.85rem' }} onClick={openAddModal}>
            <i className="bi bi-plus-lg me-2"></i>Add New
          </button>
        </div>

        {/* Filters */}
        <div className="row g-2 mb-4">
          <div className="col-md-9">
            <input type="text" className="form-control" placeholder="Search by name, code, or contact person..." style={inputStyle} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="col-md-3">
            <select className="form-select" style={inputStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Glass Table Container */}
        
        <div style={{ backgroundColor: "rgba(255, 255, 255, 0.6)", backdropFilter: "blur(12px)", borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.3)", boxShadow: "0 10px 30px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3 border-0" onClick={() => requestSort("clientCode")}>CODE {getSortIcon("clientCode")}</th>
                  <th className="py-3 border-0" onClick={() => requestSort("clientName")}>NAME {getSortIcon("clientName")}</th>
                  <th className="py-3 border-0">CONTACT</th>
                  <th className="py-3 border-0 text-center">STATUS</th>
                  <th className="pe-4 py-3 border-0 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: "0" }}>
                {currentData.map((item) => (
                  <tr key={item.id} className="align-middle">
                    <td className="ps-4 py-3 fw-bold text-primary" style={{ fontSize: "0.85rem" }}>{item.clientCode}</td>
                    <td className="py-3">
                      <div className="fw-bold text-dark" style={{ fontSize: "0.9rem" }}>{item.name || item.clientName}</div>
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>{item.email}</div>
                    </td>
                    <td className="py-3">
                      <div style={{ fontSize: "0.85rem" }}>{item.contactPerson}</div>
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>{item.mobile}</div>
                    </td>
                    <td className="text-center">
                      <span className={`badge rounded-pill ${(item.status === "Active" || item.isActive) ? "bg-success bg-opacity-10 text-success border border-success" : "bg-danger bg-opacity-10 text-danger border border-danger"} border-opacity-25`} style={{ fontSize: "0.65rem", padding: '6px 12px' }}>
                        {toCamelCase(item.status || (item.isActive ? "Active" : "Inactive"))}
                      </span>
                    </td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-sm btn-outline-primary px-3 fw-bold" style={{ fontSize: '0.75rem', borderRadius: '6px' }} onClick={() => openEditModal(item)}>Edit</button>
                        <button className={`btn btn-sm ${(item.status === "Active" || item.isActive) ? "btn-outline-danger" : "btn-outline-success"} px-3 fw-bold`} style={{ fontSize: '0.75rem', borderRadius: '6px', minWidth: '95px' }} onClick={() => handleStatusToggle(item)}>
                          {(item.status === "Active" || item.isActive) ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show</span>
              <select className="form-select form-select-sm" style={{ width: 'auto', borderRadius: '6px' }} value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                {[10, 25, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <span className="text-muted small">of <strong>{totalItems}</strong> entries</span>
            </div>
            <nav className="d-flex align-items-center">
              <button className={`page-link-custom ${currentPage === 1 ? 'disabled text-muted' : ''}`} onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}><i className="bi bi-chevron-left"></i></button>
              {totalPages > 1 && <button className={`page-link-custom ${currentPage === 1 ? 'active' : ''}`} onClick={() => setCurrentPage(1)}>1</button>}
              {getPaginationGroup().map(p => p !== 1 && p !== totalPages && (
                <button key={p} className={`page-link-custom ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
              ))}
              <button className={`page-link-custom ${currentPage === totalPages ? 'disabled text-muted' : ''}`} onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}><i className="bi bi-chevron-right"></i></button>
            </nav>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 3000 }}>
          <div className="bg-white p-4 rounded-4 shadow-lg text-center" style={{ width: "400px" }}>
            <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: "3rem" }}></i>
            <h5 className="fw-bold mt-3">Confirm Deactivation</h5>
            <p className="text-muted small">Are you sure you want to deactivate <strong>{pendingClient?.name || pendingClient?.clientName}</strong>?</p>
            <div className="d-flex gap-2 mt-4">
              <button className="btn btn-light w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-danger w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => executeStatusUpdate(pendingClient)}>Yes, Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {/* Main Modal */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.4)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000 }}>
          <div className="bg-white p-4 rounded-4 shadow-lg border-0" style={{ width: "650px", maxHeight: "90vh", overflowY: "auto" }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold m-0 text-uppercase" style={{ fontSize: '1rem', letterSpacing: '1px' }}>{isEditMode ? "Edit" : "Add"} Client</h5>
              <button className="btn-close" onClick={() => setShowModal(false)}></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Payroll Group Code</label>
                  <input type="text" className="form-control" required style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.clientCode} onChange={(e) => setFormData({ ...formData, clientCode: e.target.value })} />
                </div>
                <div className="col-md-7">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Payroll Group</label>
                  <input type="text" className="form-control" required style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.clientName} onChange={(e) => setFormData({ ...formData, clientName: e.target.value })} />
                </div>
                <div className="col-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Details/Description</label>
                  <textarea className="form-control" style={{ border: "1px solid #ddd", borderRadius: "8px" }} rows="2" value={formData.details} onChange={(e) => setFormData({ ...formData, details: e.target.value })}></textarea>
                </div>
                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Contact Person</label>
                  <input type="text" className="form-control" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.contactPerson} onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Mobile</label>
                  <input type="text" className="form-control" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Email</label>
                  <input type="email" className="form-control" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Website</label>
                  <input type="text" className="form-control" placeholder="https://www.acme.com" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                </div>
                <div className="col-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Address</label>
                  <textarea className="form-control" style={{ border: "1px solid #ddd", borderRadius: "8px" }} rows="2" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}></textarea>
                </div>
                <div className="col-12">
                  <div className="form-check form-switch mt-2">
                    <input className="form-check-input" type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
                    <label className="form-check-label small fw-bold text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Active Status</label>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 mt-4 pt-3 border-top">
                <button type="button" className="btn btn-light w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary w-100 fw-bold" style={{ backgroundColor: "#0f172a", borderRadius: '8px' }} disabled={status === "loading"}>
                  {status === "loading" ? "Saving..." : "Save Client"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Client;